
'use client';

import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import type { Service, UpsertService } from '@/types';
import { db, storage } from '@/lib/firebase';
import { collection, onSnapshot, query, orderBy, Timestamp, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import { v4 as uuidv4 } from 'uuid';

interface ServiceContextType {
    services: Service[];
    createService: (service: UpsertService) => Promise<void>;
    updateService: (serviceId: string, service: UpsertService) => Promise<void>;
    deleteService: (serviceId: string) => Promise<void>;
}

const ServiceContext = createContext<ServiceContextType | undefined>(undefined);

const safeGetDate = (timestamp: any): Date => {
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (timestamp instanceof Date) return timestamp;
    return new Date();
};

export const ServiceProvider = ({ children }: { children: ReactNode }) => {
    const [services, setServices] = useState<Service[]>([]);
    const { toast } = useToast();
    const { user, isOwner } = useAuth();

    useEffect(() => {
        const q = query(collection(db, "services"), orderBy("timestamp", "desc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const servicesData: Service[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data();
                servicesData.push({
                    id: doc.id,
                    title: data.title,
                    thumbnailUrl: data.thumbnailUrl,
                    thumbnailPath: data.thumbnailPath, // For deletion in Storage
                    author: data.author,
                    rating: data.rating,
                    reviewCount: data.reviewCount,
                    price: data.price,
                    currency: data.currency,
                    timestamp: safeGetDate(data.timestamp),
                });
            });
            setServices(servicesData);
        }, (error) => {
            console.error("Firestore snapshot error (services):", error);
            toast({
                title: "Connection Error",
                description: "Could not load services.",
                variant: "destructive",
            });
        });

        return () => unsubscribe();
    }, [toast]);
    
    const createService = async (service: UpsertService) => {
        if (!user || !isOwner) return;
        if (!service.thumbnailFile) {
            toast({ title: "Image required", description: "Please provide an image for the service.", variant: "destructive" });
            return;
        }

        try {
            // Upload image
            const thumbnailPath = `services/${user.uid}/${uuidv4()}`;
            const storageRef = ref(storage, thumbnailPath);
            await uploadBytes(storageRef, service.thumbnailFile);
            const thumbnailUrl = await getDownloadURL(storageRef);

            // Data to be saved in Firestore
            const serviceToCreate = {
                 title: service.title,
                 price: service.price,
                 rating: service.rating,
                 reviewCount: service.reviewCount,
                 thumbnailUrl,
                 thumbnailPath,
                 author: {
                     name: user.displayName,
                     avatarUrl: user.photoURL,
                 },
                 currency: 'DH',
                 timestamp: serverTimestamp(),
            }

            // Create service doc
            await addDoc(collection(db, 'services'), serviceToCreate);
            toast({ title: "Success", description: "Service created successfully." });
        } catch (error) {
            console.error("Error creating service:", error);
            toast({ title: "Error", description: "Service creation failed.", variant: "destructive" });
        }
    };
    
    const updateService = async (serviceId: string, serviceData: UpsertService) => {
        if (!user || !isOwner) return;

        try {
            const serviceRef = doc(db, 'services', serviceId);
            const updatePayload: any = {
                title: serviceData.title,
                price: serviceData.price,
                rating: serviceData.rating,
                reviewCount: serviceData.reviewCount,
            };

            if (serviceData.thumbnailFile) {
                 const thumbnailPath = `services/${user.uid}/${uuidv4()}`;
                 const storageRef = ref(storage, thumbnailPath);
                 await uploadBytes(storageRef, serviceData.thumbnailFile);
                 const thumbnailUrl = await getDownloadURL(storageRef);
                 updatePayload.thumbnailUrl = thumbnailUrl;
                 updatePayload.thumbnailPath = thumbnailPath;
            }

            await updateDoc(serviceRef, updatePayload);
            toast({ title: "Success", description: "Service updated." });

        } catch (error) {
             console.error("Error updating service:", error);
             toast({ title: "Error", description: "Update failed.", variant: "destructive" });
        }
    };

    const deleteService = async (serviceId: string) => {
        if (!user || !isOwner) return;
        try {
            const serviceToDelete = services.find(s => s.id === serviceId);
            if(serviceToDelete && serviceToDelete.thumbnailPath) {
                await deleteObject(ref(storage, serviceToDelete.thumbnailPath));
            }
            await deleteDoc(doc(db, 'services', serviceId));
            toast({ title: "Success", description: "Service deleted." });
        } catch(error) {
            console.error("Error deleting service:", error);
            toast({ title: "Error", description: "Deletion failed.", variant: "destructive" });
        }
    };

    return (
        <ServiceContext.Provider value={{ services, createService, updateService, deleteService }}>
            {children}
        </ServiceContext.Provider>
    );
};

export const useService = () => {
    const context = useContext(ServiceContext);
    if (context === undefined) {
        throw new Error('useService must be used within a ServiceProvider');
    }
    return context;
};
